import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import AnalyticsContext from 'contexts/AnalyticsContext';
import BaseViewsContext from 'contexts/BaseViewsContext';
import DashboardContext from 'contexts/DashboardContext';
import ReportContext from 'contexts/ReportContext';
import { getDrillDownFieldName } from 'hooks/useDrillDownFieldName';
import usePopup from 'hooks/usePopup';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import FilterPlateForm from 'components/FilterPlateForm';
import {
  FILTER_PLATE_HEIGHT,
  FILTER_PLATE_MAX_WIDTH,
} from 'components/FilterPlates/FilterPlate/Interactive';
import InlineDialog from 'components/InlineDialog';

const EmptyPlate = styled.div<{ isOpen: boolean }>`
  max-width: ${FILTER_PLATE_MAX_WIDTH}px;
  height: ${FILTER_PLATE_HEIGHT}px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0px 8px 0px 12px;
  border-radius: 8px;
  border: 1px dashed var(--color-border-primary);
  cursor: pointer;
  background-color: ${(props) =>
    props.isOpen ? 'var(--color-bg-selected)' : 'var(--color-bg-tertiary)'};

  &:hover {
    opacity: 0.7;
  }

  svg {
    height: 16px;
  }
`;

const EmptyPinnedFilterPlate = ({
  pin,
}: {
  pin: ReportPinnedFilters.Field;
}) => {
  const { report } = useContext(ReportContext);
  const { dashboard } = useContext(DashboardContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { isOpen, open, close } = usePopup();
  const label = getDrillDownFieldName(pin, baseViews);
  const color = isOpen ? 'var(--color-info-v2)' : 'var(--color-text-secondary)';

  const onClick = useCallback(() => {
    if (isOpen) {
      close();
      return;
    }

    if (report) {
      trackEvent('Report - Pinned Filter Opened', {
        reportName: report.name,
        reportId: report.id,
        field: pin.field,
      });
    } else if (dashboard) {
      trackEvent('Dashboard - Pinned Filter Opened', {
        dashboardName: dashboard.name,
        dashboardId: dashboard.id,
        field: pin.field,
      });
    }
    open();
  }, [close, dashboard, isOpen, open, pin.field, report, trackEvent]);

  return (
    <InlineDialog
      isOpen={isOpen}
      onClose={close}
      content={
        <FilterPlateForm close={close} isScope={false} initialField={pin} />
      }
    >
      <EmptyPlate
        isOpen={isOpen}
        onClick={onClick}
        data-testid={`pinned-filter-${pin.field}`}
      >
        <Typography.Body type="Button Text" color={color} isEllipsis>
          {label}
        </Typography.Body>
        <Icon icon={'chevron-down'} color={color} />
      </EmptyPlate>
    </InlineDialog>
  );
};

export default EmptyPinnedFilterPlate;
